// Core
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Third-Party Libraries
import QrCodeIcon from '@mui/icons-material/QrCode';
import Switch from '@mui/material/Switch';
import { CircularProgress, TextField } from '@mui/material';
import _ from 'lodash';
import Axios from 'axios';

// Components
import GridContainer from 'components/molecules/GridContainer';
import ModalView from 'components/atoms/ModalView';
import PrimaryButton from 'components/atoms/CustomButtons';
import CustomImagePicker from 'components/atoms/CustomImagePicker';
import { useSnackbar } from 'components/atoms/SnackbarProvider';
import ClassmatesListWrapper from 'components/atoms/ClassmatesListWrapper';
import DeleteModal from 'components/atoms/DeleteModal';

// Styles and Constants
import './index.scss';
import { colors } from 'assets/styles/colors';

// Utilities
import { CLASS_JOIN, S3UPLOAD } from 'utils/restAPIs';
import {
    CLASSNAME,
    ClassroomData2Keys,
    ClassroomDataKeys,
    ClassroomImageType,
    ClassroomPermissionData,
    ClassroomPermissionModal,
    ClassroomPermissionModalDescriptions,
    ClassroomSettingsConstData,
    ClassroomSettingsData,
    ClassroomSettingsDataType,
    ClassroomSettingsIcons,
    CursorType,
    LEAVE_ROOM_MODAL,
    MODAL_STYTLE,
    PrivacyOptions,
    SnackBarMessages,
    StatusType
} from 'utils/constants';
import { fetchData } from 'utils/fetch';
import { loadActiveClassroomLocalStorage, loadUserFromLocalStorage } from 'utils/commonUtils';
import { restAPIs } from 'utils/restAPIs';
import { useUpdateUser } from 'utils/customHooks';

// Assets
import class_logo from '../../assets/images/class_logo.png'
import class_cover from '../../assets/images/class_cover.png'

// Redux Actions and State
import { clearActiveClassroom, setAcademicAccess, setActiveClassRoom, setPostAccess } from '../../redux/slices/setClassroom/classRoomSlice';


const ClassRoomSettings: React.FC = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showSnackbar } = useSnackbar();
    const getUpdateUser = useUpdateUser()
    const [boardData, setBoardData] = useState<any>([]);
    const [editItem, setEditItem] = useState<any>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [open, setOpen] = useState<boolean | string>(false);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [logoImage, setLogoImage] = useState<File | null>(null);
    const [coverImage, setCoverImage] = useState<File | null>(null);
    const [coverLoading, setCoverLoading] = useState(false);
    const [logoLoading, setLogoLoading] = useState(false);
    const [showLeave, setShowLeave] = useState(false);
    const { classroomData } = useSelector((state: any) => state.classRoom) || {};
    const activeClassroom = loadActiveClassroomLocalStorage()
    const user = loadUserFromLocalStorage();
    const classRooms = user?.classRooms
    const { isAdmin } = classroomData || {}
    const link = CLASS_JOIN + activeClassroom?._id;
    const [isExpanded, setIsExpanded] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const textRef = useRef<HTMLDivElement | null>(null);

    const toggleReadMore = () => setIsExpanded(!isExpanded);

    const leaveData = { title: `${LEAVE_ROOM_MODAL.title} ${activeClassroom?.name}`, desc: LEAVE_ROOM_MODAL.desc, button: LEAVE_ROOM_MODAL.button }

    const data = [
        {
            key: ClassroomDataKeys.Name,
            value: activeClassroom?.name
        },
        {
            key: ClassroomDataKeys.Id,
            value: `#${activeClassroom?.classRoomId}`
        },
        {
            key: ClassroomDataKeys.About,
            value: activeClassroom?.about, placeholder: ClassroomDataKeys.Placeholder
        }
    ];

    const data2 = [
        {
            key: ClassroomData2Keys.JoiningLink,
            value: link
        },
        {
            key: ClassroomData2Keys.QrCode,
            value: ClassroomData2Keys.value
        },
    ];

    const permissionData = [
        {
            key: ClassroomPermissionData.WriteOnBoard,
            value: activeClassroom?.memberPermissions?.board ? ClassroomPermissionData.Everyone : ClassroomPermissionData.OnlyAdmins
        },
        {
            key: ClassroomPermissionData.Academic,
            value: activeClassroom?.memberPermissions?.academic ? ClassroomPermissionData.Everyone : ClassroomPermissionData.OnlyAdmins
        },
    ];

    const settingsData = [
        {
            value: ClassroomSettingsData.ChangeLogo
        },
        {
            value: ClassroomSettingsData.ChangeCover
        },
        {
            key: ClassroomSettingsData.Privacy,
            value: activeClassroom?.public ? ClassroomSettingsData.Public : ClassroomSettingsData.Private
        },
        {
            key: ClassroomSettingsData.NewsUpdates,
            value: ClassroomSettingsData.NewsDescription
        },
        {
            key: `${ClassroomSettingsData.LeaveClassroom} ${activeClassroom?.name}`,
            value: ClassroomSettingsData.LeaveClassroomDescription
        },
    ];

    const privacyData = [
        {
            icon: ClassroomSettingsIcons.Earth,
            title: PrivacyOptions.Everyone
        },
        {
            icon: ClassroomSettingsIcons.Lock,
            title: open == ClassroomSettingsDataType.PrivacyData ? PrivacyOptions.AdminApproval : PrivacyOptions.OnlyAdmins
        }
    ];


    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
            const lines = element.scrollHeight / lineHeight;
            if (lines > 3) {
                setShowReadMore(true);
            }
            else {
                setShowReadMore(false);
            }
        }
    }, [activeClassroom?.about]);

    const getUniversityTags = () => {
        let filterTags = activeClassroom?.templateId?.filters || {};
        const clonedFilterTags = { ...filterTags };
        if (typeof clonedFilterTags === 'object' && clonedFilterTags !== null) {
            delete clonedFilterTags['filterId'];
        }
        const myArr = Object.values(clonedFilterTags);
        const finalmyArr = myArr.filter(item => item);

        setBoardData(finalmyArr);
    };

    useEffect(() => {
        getUniversityTags();
    }, [localStorage.getItem('activeClassroom')]);

    const onDataClick = (item: any) => {
        setEditItem(item)
        setOpen(ClassroomSettingsDataType.EditData)
    }

    const updateClassroom = async (params: any) => {
        setLoading(true)
        const res = await fetchData(restAPIs.getClassRoomPrivacy(activeClassroom?._id, params), navigate)
        let temp = [...classRooms];
        if (res.status === 200 && res.data) {
            dispatch(setActiveClassRoom(res?.data))
            setEditItem(null);
            setOpen(false)
            showSnackbar(SnackBarMessages.ClassroomUpdated, StatusType.success)
            setLoading(false)
        }
        else {
            showSnackbar(SnackBarMessages.SomethingWentWrong, StatusType.error)
            setLoading(false)
        }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        let params = {
            [editItem?.key?.toLowerCase()]: editItem?.value,
        };
        updateClassroom(params)
    }

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        if (value?.length < CLASSNAME || name !== ClassroomSettingsConstData.Name) {
            setEditItem({ ...editItem, value: event.target.value });
            setError(null)
        }
        else {
            setError(`Maximum length allowed is ${CLASSNAME} characters`)
        }
    };

    const handleCopy = async (item: any) => {
        try {
            await navigator.clipboard.writeText(item?.value);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy text: ', error);
        }
    };


    const RenderModal = () => (
        <div className='_classroom_edit_modal'>
            <div className='__modal'>
                <form
                    onSubmit={(e) => handleSubmit(e)}
                    className='login-form'>
                    {editItem?.key === ClassroomSettingsConstData.Name ? (
                        <TextField
                            label={editItem?.key}
                            fullWidth
                            variant="outlined"
                            error={!!error}
                            helperText={error}
                            value={editItem?.value}
                            onChange={handleChange}
                            margin="normal"
                            name={editItem?.key}
                            className="input-field"
                            InputLabelProps={{
                                style: { fontSize: '13px', fontWeight: 400, color: "#B4B4B4" },
                            }}
                        />) :
                        <div className="input-group">
                            <textarea
                                value={editItem?.value}
                                onChange={handleChange}
                                placeholder={editItem?.placeholder} />
                        </div>}
                    <div className='button-wrapper'>
                        <PrimaryButton
                            fullWidth={false}
                            isLoading={isLoading} >
                            {ClassroomSettingsConstData.Update}
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    )

    const onSetPrivacy = (value: boolean) => {
        let params = {
            public: value,
        };
        updateClassroom(params);
    };

    const onPrivacyClick = (key: string, value: boolean) => {
        const type = key.toLowerCase() === ClassroomSettingsConstData.academic ?
            ClassroomSettingsConstData.academic :
            ClassroomSettingsConstData.Post;
        let params = {};
        if (type == ClassroomSettingsConstData.Post) {
            params = {
                memberPermissions: {
                    academic: activeClassroom?.memberPermissions?.academic,
                    board: value,
                },
            };
            if (isAdmin) {
                dispatch(setPostAccess(true))
            } else {
                dispatch(setPostAccess(value))
            }
        } else {
            params = {
                memberPermissions: {
                    academic: value,
                    board: activeClassroom?.memberPermissions?.board,
                },
            };
            if (isAdmin) {
                dispatch(setAcademicAccess(true))
            } else {
                dispatch(setAcademicAccess(value))
            }
        }
        updateClassroom(params);
    }

    const RenderPrivacyOptions = () => {
        const tempValue = open == ClassroomSettingsDataType.PrivacyData ?
            activeClassroom?.public :
            editItem?.key == permissionData[0].key ?
                activeClassroom?.memberPermissions?.board :
                activeClassroom?.memberPermissions?.academic
        return (
            <div>
                {privacyData?.map((item, index) => (
                    <div
                        onClick={() => open == ClassroomSettingsDataType.PrivacyData ?
                            onSetPrivacy(index == 0) :
                            onPrivacyClick(editItem?.key, index == 0)}
                        className='privacy_option'>
                        <i className={item.icon} />
                        {(index === 0 && tempValue || index === 1 && !tempValue) &&
                            <i className={ClassroomSettingsIcons.CheckCircle} />}
                        <div>{item.title}</div>
                    </div>
                ))}
            </div>
        )
    }

    const RenderPermissionModal = () => (
        <div style={{ minWidth: '350px' }} className='_classroom_edit_modal _permission'>
            <div className='__modal'>
                <div className='desc'>{permissionModalDesc}</div>
                {RenderPrivacyOptions()}
            </div>
        </div>
    )

    const RenderTooltip = (item: any) => (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <i
                style={{ color: colors?.secondary, cursor: CursorType.Pointer }}
                onMouseLeave={() => setCopySuccess(false)}
                className="fi fi-rr-copy" />
            {copySuccess && (
                <span className='copy_success_span'>
                    {ClassroomSettingsConstData.LinkCopied}
                </span>
            )}
        </div>
    )

    const onS3Upload = async (Image: File, imagetype: string) => {
        let params = {};
        if (imagetype == ClassroomImageType.Cover) {
            setCoverLoading(true);
        } else {
            setLogoLoading(true);
        }
        if (Image) {

            let formData = new FormData();
            if (imagetype == ClassroomImageType.Cover) {
                formData.append('file', Image, Image.name);
            }
            else if (imagetype == ClassroomImageType.Logo) {
                formData.append('file', Image, Image.name);
            }
            const config = {
                headers: {
                    'Authorization': user?.token,
                },
            };
            const response = await Axios.post(
                S3UPLOAD + `classroom/${activeClassroom?._id}/${imagetype}/${Image.name}`,
                formData,
                config,
            );
            if (response.status == 200) {
                if (imagetype == ClassroomImageType.Cover) {
                    params = {
                        cover: `classroom/${activeClassroom?._id}/${imagetype}/${Image.name}`,
                    };
                } else {
                    params = {
                        logo: `classroom/${activeClassroom?._id}/${imagetype}/${Image.name}`,
                    };
                }
                updateClassroom(params);
            }
            setLogoLoading(false);
            setCoverLoading(false);
        }
    };

    const setUpadest = () => {
        if (!isLoading) {
            let params = {
                getUpdatesFromTemplates: !activeClassroom?.getUpdatesFromTemplates,
            };
            updateClassroom(params);
        }
    };

    const changePrivacy = (item: any) => {
        setOpen(ClassroomSettingsDataType.PrivacyData);
        setEditItem(item)
    };

    const onLeave = async () => {
        setShowLeave(false);
        let params = {
            studentId: user?._id?.length > 0 && user?._id,
        };

        const res = await fetchData(restAPIs.getLeaveRoom(activeClassroom?._id, params, true), navigate)
        if (res?.status === 200) {
            showSnackbar(SnackBarMessages.ClassroomLeft, StatusType.success)
            getUpdateUser()
            dispatch(clearActiveClassroom())
            navigate('/home')
        }
        else {
            showSnackbar(SnackBarMessages.SomethingWentWrong, StatusType.error)
        }
    };

    const onClickSettingsData = (index: number, item?: any) => {
        if (isAdmin) {
            if (index == 2) {
                changePrivacy(item)
            }
        }
        if (index == 4) {
            setShowLeave(true)
        }
    }

    const getCursor = (index: number) => {
        if (index == 3) {
            return ''
        }
        else if (index == 4) {
            return CursorType.Pointer
        }
        else {
            return isAdmin ? CursorType.Pointer : ''
        }
    }

    const permissionOnClick = (item: any) => {
        if (isAdmin) {
            setOpen(ClassroomSettingsDataType.PermissionData)
            setEditItem(item)
        }
    }

    const renderSettingsDataCard = (item: any, index: number) => {
        return (
            <div
                onClick={() => onClickSettingsData(index, item)}
                key={index}
                className='wrapper'
                style={{ borderBottom: index == settingsData.length - 1 ? 'none' : '', cursor: getCursor(index) }}>
                <div>
                    <div
                        className={index > 2 ? 'value' : 'key'}
                        style={{ paddingBottom: index <= 2 ? '0px' : '5px', color: index == 4 ? 'red' : '' }}>
                        {item?.key}
                    </div>
                    <div
                        className={index > 2 ? 'key' : 'value'}
                        style={{ paddingBottom: index <= 2 ? '5px' : '0px' }}>
                        {item?.value}
                        {index == 2 &&
                            <i style={{ color: colors?.secondary, fontSize: '12px', marginLeft: '5px' }}
                                className={activeClassroom?.public ? 'fi fi-rr-earth-americas:' : 'fi fi-sr-lock'} />}
                    </div>
                </div>
                {(isAdmin || index == 4) &&
                    index == 3 ?
                    <Switch
                        onChange={setUpadest}
                        checked={activeClassroom?.getUpdatesFromTemplates} /> :
                    (index == 2 || index == 4) ?
                        <i style={{ color: colors?.secondary, cursor: CursorType.Pointer }}
                            className={index == 2 ? isAdmin ? ClassroomSettingsIcons.AngleRight : '' : ClassroomSettingsIcons.AngleRight} /> :
                        <CustomImagePicker
                            imageType={index == 0 ? ClassroomImageType.Logo : ClassroomImageType.Cover}
                            onS3Upload={onS3Upload}
                            id={`picker-${index}`}
                            setSelectedImage={index == 0 ? setLogoImage : setCoverImage}>
                            {(index == 0 && logoLoading || index == 1 && coverLoading) ?
                                <CircularProgress
                                    size={16}
                                    sx={{ color: colors?.primary }} /> :
                                <i style={{ color: colors?.secondary }}
                                    className={ClassroomSettingsIcons.Camera} />}
                        </CustomImagePicker>
                }
            </div>
        )
    }

    const permissionModalTitle = open == ClassroomSettingsDataType.PrivacyData ? ClassroomPermissionModal.JoinClassroom : editItem?.key == permissionData[0].key ? ClassroomPermissionModal.WriteOnBoard : ClassroomPermissionModal.CreateAcademicActivities
    const permissionModalDesc = editItem?.key == permissionData[0].key ? ClassroomPermissionModalDescriptions.WriteOnBoard : ClassroomPermissionModalDescriptions.CreateAcademicActivities

    return (
        <GridContainer
            pageTitle={'Classroom Settings'}
            backButton
            leftGridPartition={3 / 4}
        >
            <div className='classroom_settings_container'>
                <div style={{
                    backgroundImage: `linear-gradient(to bottom,rgba(0,0,0,0),rgba(0,0,0,0.0) ,rgba(0,0,0,0.2),rgba(0,0,0,.4),rgba(0,0,0,.7)),
                        url(${activeClassroom?.cover || class_cover})`,
                }}
                    className="class-room-detail-cover detail-cover">
                    <img
                        src={activeClassroom?.logos?.small?.length > 0 ? activeClassroom?.logos?.small : class_logo}
                        className='class_logo' />
                    <div className='detail-cover-wrapper'>
                        <span className='detail-cover-text'>{activeClassroom?.name}</span>
                        <span className='detail-cover-text id'>#{activeClassroom?.classRoomId}</span>
                    </div>
                </div>
                <div className='_classroom_settings'>
                    {data?.map((item, index) => (
                        <div key={index} className='wrapper'>
                            <div>
                                <div className='key'>{item?.key}</div>
                                {index == 2 ? <div>
                                    <div
                                        className={`value ${isExpanded ? 'expanded' : 'collapsed'}`}
                                        ref={textRef}
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {(index !== 2 || item?.value?.length > 0) ? item?.value : ClassroomSettingsConstData.NoDescription}
                                    </div>
                                    {showReadMore && (
                                        <span className="read-more" onClick={toggleReadMore}>
                                            {isExpanded ? ClassroomSettingsConstData.ReadLess : ClassroomSettingsConstData.ReadMore}
                                        </span>
                                    )}
                                </div> :
                                    <div className='value'>
                                        {(index != 2 || item?.value?.length > 0) ? item?.value : ClassroomSettingsConstData.NoDescription}
                                    </div>}
                            </div>
                            {isAdmin && index !== 1 && (
                                <i
                                    onClick={() => onDataClick(item)}
                                    style={{ color: colors?.secondary, cursor: CursorType.Pointer }}
                                    className={ClassroomSettingsIcons.Pencil} />
                            )}
                        </div>
                    ))}
                    <div className='wrapper'>
                        <div>
                            <div className='key'>{ClassroomSettingsConstData.University}</div>
                            <div className='value'>
                                {boardData?.length > 0 ? (
                                    <>
                                        {boardData?.map((item: string, index: number) => (
                                            <div className='_board_tag' key={index}>{item}</div>
                                        ))}
                                    </>) : <div className='value'>{ClassroomSettingsConstData.NoUniversity}</div>}
                            </div>
                        </div>
                    </div>
                    {data2?.map((item, index) => (
                        <div
                            onClick={() => index == 0 ? handleCopy(item) : navigate('/qr-code')}
                            key={index}
                            className='wrapper'
                            style={{ borderBottom: index == data2.length - 1 ? 'none' : '', cursor: CursorType.Pointer }}>
                            <div>
                                <div className='key'>{item?.key}</div>
                                <div className='value'>{item?.value}</div>
                            </div>
                            {index == 0 ? (
                                RenderTooltip(item)
                            ) :
                                (
                                    <QrCodeIcon style={{ color: colors.secondary, fontSize: '16px', cursor: CursorType.Pointer }} />)}
                        </div>
                    ))}
                </div>
                <div className='title'>{ClassroomSettingsConstData.ClassmatesPermission}</div>
                <div className='_classroom_settings'>
                    {permissionData?.map((item, index) => (
                        <div
                            onClick={() => permissionOnClick(item)}
                            key={index} className='wrapper'
                            style={{ borderBottom: index == data2.length - 1 ? 'none' : '', cursor: isAdmin ? CursorType.Pointer : 'default' }}>
                            <div>
                                <div className='key'>{item?.key}</div>
                                <div className='value'>{item?.value}</div>
                            </div>
                            {isAdmin && (
                                <i style={{ color: colors?.secondary, cursor: CursorType.Pointer }} className={ClassroomSettingsIcons.AngleRight} />
                            )}
                        </div>
                    ))}
                </div>
                <div className='title'>{ClassroomSettingsConstData.ClassroomSettings}</div>
                <div className='_classroom_settings'>
                    {settingsData?.map((item, index) => {
                        if (!isAdmin && index != 4 && index != 2) return;
                        if (item?.key) {
                            return (
                                <div>
                                    {renderSettingsDataCard(item, index)}
                                </div>
                            )
                        }
                        else {
                            return (
                                <CustomImagePicker
                                    imageType={index == 0 ? ClassroomImageType.Logo : ClassroomImageType.Cover}
                                    onS3Upload={onS3Upload}
                                    id={`picker-${index}`}
                                    setSelectedImage={index == 0 ? setLogoImage : setCoverImage}>
                                    {renderSettingsDataCard(item, index)}
                                </CustomImagePicker>
                            )
                        }
                    })}
                </div>
            </div>
            <div className='_classroom_members'>
                <ClassmatesListWrapper fromSettings />
            </div>
            <DeleteModal
                open={showLeave}
                onSubmit={onLeave}
                data={leaveData}
                modalStyle={MODAL_STYTLE}
                handleClose={() => setShowLeave(false)}
            />
            <ModalView
                title={`Edit ${editItem?.key}`}
                open={open == ClassroomSettingsDataType.EditData}
                handleClose={() => { setEditItem(null); setOpen(false) }}
                modalStyle={MODAL_STYTLE}>
                {RenderModal()}
            </ModalView>
            <ModalView
                title={permissionModalTitle}
                open={open == ClassroomSettingsDataType.PermissionData || open == ClassroomSettingsDataType.PrivacyData}
                handleClose={() => { setEditItem(null); setOpen(false) }}
                modalStyle={MODAL_STYTLE}>
                {RenderPermissionModal()}
            </ModalView>
        </GridContainer>
    );
};

export default ClassRoomSettings;
